import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Button, Badge, Table, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    MdShoppingCart, MdAddCircle, MdRefresh,
    MdCategory, MdInventory, MdWarning,
    MdLocalShipping, MdAssignment, MdArrowBack
} from "react-icons/md";
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as ReTooltip,
    LineChart, Line, Legend, PieChart, Pie, Cell
} from 'recharts';
 
const InventoryDashboard = () => {
    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const userData = sessionStorage.getItem("user");
    const userObj = userData ? JSON.parse(userData) : {};
    const schoolId = userObj.school_id;
 
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total_categories: 0,
        total_items: 0,
        available_stock: 0,
        issued_items: 0,
        low_stock_alerts: 0
    });
    const [categories, setCategories] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [monthlyTrends, setMonthlyTrends] = useState([]);
 
    // Filters
    const currentYear = new Date().getFullYear().toString();
    const currentMonthName = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][new Date().getMonth()];
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState(currentMonthName);
    const [selectedCategory, setSelectedCategory] = useState(null);
 
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthIndexMap = { "Jan": 0, "Feb": 1, "Mar": 2, "Apr": 3, "May": 4, "Jun": 5, "Jul": 6, "Aug": 7, "Sep": 8, "Oct": 9, "Nov": 10, "Dec": 11 };
 
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, catRes, itemsRes, transRes] = await Promise.all([
                axios.get(`${baseUrl}/inventoryreports/dashboard/${schoolId}`),
                axios.post(`${baseUrl}/inventorycategory/`, { action: "READ", school_id: schoolId }),
                axios.post(`${baseUrl}/inventoryitem/`, { action: "READ", school_id: schoolId }),
                axios.post(`${baseUrl}/inventorytransaction/`, { action: "READ", school_id: schoolId, type: "ISSUE" })
            ]);
 
            setStats(statsRes.data || {});
            setCategories(catRes.data || []);
            setAllItems(itemsRes.data || []);
            setTransactions(transRes.data || []);
 
            // Fetch monthly trends
            try {
                const trendsRes = await axios.get(`${baseUrl}/inventoryreports/monthly-trends/${schoolId}?year=${selectedYear}`);
                setMonthlyTrends(trendsRes.data || []);
            } catch (trendErr) {
                console.warn("Monthly trends not available:", trendErr);
                setMonthlyTrends([]);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, schoolId, selectedYear]);
 
    useEffect(() => {
        fetchData();
    }, [fetchData]);
 
    // Compute category-level statistics from items
    const categoryStats = useMemo(() => {
        const activeCategories = categories.filter(c => c.is_active === 'A');
        return activeCategories.map(cat => {
            const catItems = allItems.filter(item => item.category_id === cat.category_id && item.is_active === 'A');
            const totalStock = catItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
            const lowStockCount = catItems.filter(item => (item.stock_status || '').toUpperCase().includes('LOW')).length;
            return {
                ...cat,
                itemCount: catItems.length,
                totalStock,
                lowStockCount
            };
        });
    }, [categories, allItems]);
 
    // Filter items by selected category
    const filteredItems = useMemo(() => {
        const activeItems = allItems.filter(item => item.is_active === 'A');
        if (!selectedCategory) return activeItems;
        return activeItems.filter(item => item.category_id === selectedCategory);
    }, [allItems, selectedCategory]);
 
    // Compute issued/returned counts per item for the selected month/year
    const itemTransactionStats = useMemo(() => {
        const monthIdx = monthIndexMap[selectedMonth];
        const yearNum = parseInt(selectedYear);
 
        const stats = {};
        transactions.forEach(t => {
            if (!t.issue_date) return;
            const d = new Date(t.issue_date);
            if (d.getMonth() === monthIdx && d.getFullYear() === yearNum) {
                if (!stats[t.item_id]) {
                    stats[t.item_id] = { issued: 0, returned: 0 };
                }
                stats[t.item_id].issued += (t.quantity || 0);
                stats[t.item_id].returned += (t.returned_quantity || 0);
            }
        });
        return stats;
    }, [transactions, selectedMonth, selectedYear]);
 
    // Monthly chart data from API
    const chartData = useMemo(() => {
        return monthNames.map(m => {
            const found = monthlyTrends.find(t => t.month?.toLowerCase() === m.toLowerCase());
            return {
                month: m,
                purchased: found ? parseInt(found.purchase_qty || 0) : 0,
                issued: found ? parseInt(found.issue_qty || 0) : 0
            };
        });
    }, [monthlyTrends]);
 
    const formatCurrency = (val) => {
        if (!val && val !== 0) return "₹0";
        if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
        if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
        return `₹${Number(val).toFixed(0)}`;
    };
 
    // Category color palette for cards
    const catColors = useMemo(() => ['#6366f1', '#ff9f43', '#10b981', '#0461FE', '#f43f5e', '#8b5cf6', '#06b6d4', '#ec4899'], []);
 
    const pieChartData = useMemo(() => {
        if (!selectedCategory) {
            return categoryStats.map((cat, idx) => ({
                id: cat.category_id,
                name: cat.category_name,
                value: cat.totalStock || 0,
                color: catColors[idx % catColors.length],
                type: 'category'
            }));
        }
        const catIdx = categoryStats.findIndex(c => c.category_id === selectedCategory);
        const baseColor = catIdx >= 0 ? catColors[catIdx % catColors.length] : catColors[0];
       
        const getShade = (hex, factor) => {
            if (!hex || hex.length !== 7) return hex;
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            const newR = Math.min(255, Math.round(r + (255 - r) * factor));
            const newG = Math.min(255, Math.round(g + (255 - g) * factor));
            const newB = Math.min(255, Math.round(b + (255 - b) * factor));
            return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
        };
 
        const totalItems = filteredItems.length;
 
        return filteredItems.map((item, idx) => {
            const factor = totalItems > 1 ? (idx / totalItems) * 0.75 : 0;
            return {
                id: item.item_id,
                name: item.item_name,
                value: item.quantity || 0,
                color: getShade(baseColor, factor),
                type: 'item',
                unit: item.unit,
                stock_status: item.stock_status
            };
        });
    }, [selectedCategory, categoryStats, filteredItems, catColors]);
 
    return (
        <div className="pageBody inv-dashboard-wrapper" style={{ overflowY: 'auto', height: 'calc(100vh - 100px)', paddingBottom: '30px' }}>
            <Container fluid className="px-3 py-2">
 
                {/* Header Bar */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 inv-header-bar px-4 py-2 bg-white rounded-3 shadow-sm border border-light">
                    <div className="d-flex align-items-center gap-3">
                        <div className="inv-pulse-dot"><span></span></div>
                        <div>
                            <h5 className="fw-bold mb-0 text-dark" style={{ fontSize: '15px' }}>Inventory Dashboard</h5>
                            <span className="text-muted small" style={{ fontSize: '10.5px' }}>Real-time Stock & Category Intelligence</span>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="light" className="inv-refresh-btn shadow-sm py-1 px-2" onClick={fetchData} disabled={loading} style={{ height: '32px' }}>
                            {loading ? <Spinner size="sm" animation="border" /> : <MdRefresh size={18} />}
                        </Button>
                        <Button className="primaryBtn shadow-sm d-flex align-items-center gap-1 px-3 py-1 border-0 rounded-3" onClick={() => navigate("/addinventoryitem")} style={{ height: '32px', fontSize: '12px', background: 'var(--primaryColor)' }}>
                            <MdAddCircle size={15} /> Add Item
                        </Button>
                        <Button className="secondaryBtn shadow-sm d-flex align-items-center gap-1 px-3 py-1 rounded-3" onClick={() => navigate("/purchaseentry")} style={{ height: '32px', fontSize: '12px', borderColor: 'var(--primaryColor)', color: 'var(--primaryColor)', marginRight: '0' }}>
                            <MdShoppingCart size={15} /> Purchase Entry
                        </Button>
                    </div>
                </div>
 
 
                {/* Filter Row */}
                <div className="d-flex flex-wrap align-items-center gap-2 mb-3 px-2">
                    <span className="fw-bold" style={{ fontSize: '11px', color: 'var(--primaryColor)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Year:</span>
                    {["2026", "2025"].map(y => (
                        <Button key={y} className={`inv-filter-pill ${selectedYear === y ? 'active' : ''}`} onClick={() => setSelectedYear(y)}>{y}</Button>
                    ))}
                    <span className="ms-3 fw-bold" style={{ fontSize: '11px', color: 'var(--primaryColor)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Month:</span>
                    {monthNames.map(m => (
                        <Button key={m} className={`inv-filter-pill ${selectedMonth === m ? 'active' : ''}`} onClick={() => setSelectedMonth(m)}>{m}</Button>
                    ))}
                </div>
 
                <Row className="g-3 mb-3">
                    {/* Section 2: Category Cards */}
                    <Col md={4} lg={3}>
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                            <Card.Header className="bg-transparent border-0 pt-3 px-3 pb-2 d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold mb-0" style={{ fontSize: '13px', borderLeft: '3px solid var(--primaryColor)', paddingLeft: '8px' }}>
                                    {selectedCategory ? (
                                        <span className="d-flex align-items-center gap-2">
                                            <Button variant="link" className="p-0 text-decoration-none d-flex align-items-center" onClick={() => setSelectedCategory(null)} style={{ color: 'var(--primaryColor)' }}>
                                                <MdArrowBack size={16} className="me-1" /> Back
                                            </Button>
                                        </span>
                                    ) : "Categories"}
                                </h6>
                            </Card.Header>
                            <Card.Body className="p-0" style={{ height: '350px', overflowY: 'auto' }}>
                                <div className="d-flex flex-column gap-2 px-3 pb-3 pt-1">
                                    {/* All Categories Button */}
                                    <Card
                                        className={`inv-cat-card border-0 shadow-sm ${!selectedCategory ? 'active' : ''}`}
                                        onClick={() => setSelectedCategory(null)}
                                        style={{ cursor: 'pointer', borderTop: `3px solid var(--primaryColor)`, flexShrink: 0 }}
                                    >
                                        <Card.Body className="p-2 d-flex align-items-center gap-3">
                                            <div className="inv-cat-icon mb-0 ms-0 me-0" style={{ width: '32px', height: '32px', fontSize: '16px', color: 'var(--primaryColor)', background: 'rgba(4, 97, 254, 0.1)' }}><MdCategory /></div>
                                            <div className="text-start flex-grow-1">
                                                <div className="text-muted" style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', lineHeight: '1', marginBottom: '4px' }}>All Items</div>
                                                <div className="fw-bold" style={{ fontSize: '14px', color: 'var(--primaryColor)', lineHeight: '1' }}>{allItems.filter(i => i.is_active === 'A').length}</div>
                                            </div>
                                        </Card.Body>
                                    </Card>
 
                                    {categoryStats.map((cat, idx) => (
                                        <Card
                                            key={cat.category_id}
                                            className={`inv-cat-card border-0 shadow-sm ${selectedCategory === cat.category_id ? 'active' : ''}`}
                                            onClick={() => setSelectedCategory(cat.category_id)}
                                            style={{ cursor: 'pointer', borderTop: `3px solid ${catColors[idx % catColors.length]}`, flexShrink: 0 }}
                                        >
                                            <Card.Body className="p-2 d-flex align-items-center gap-3">
                                                <div className="inv-cat-icon mb-0 ms-0 me-0" style={{ width: '32px', height: '32px', fontSize: '16px', color: catColors[idx % catColors.length], background: `${catColors[idx % catColors.length]}18` }}>
                                                    <MdInventory />
                                                </div>
                                                <div className="text-start flex-grow-1">
                                                    <div className="text-muted text-truncate" style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', lineHeight: '1', marginBottom: '4px', maxWidth: '120px' }} title={cat.category_name}>
                                                        {cat.category_name}
                                                    </div>
                                                    <div className="fw-bold d-flex align-items-center justify-content-between" style={{ fontSize: '14px', color: catColors[idx % catColors.length], lineHeight: '1' }}>
                                                        <span>{cat.itemCount}</span>
                                                        {cat.lowStockCount > 0 && (
                                                            <Badge bg="danger" pill style={{ fontSize: '8px' }}>{cat.lowStockCount} low</Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
 
                    {/* Section 3: Items Drill-Down Pie Chart */}
                    <Col md={8} lg={9}>
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                            <Card.Header className="bg-transparent border-0 pt-3 px-3 pb-0 d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold mb-0" style={{ fontSize: '13px', borderLeft: '3px solid var(--primaryColor)', paddingLeft: '8px' }}>
                                    Items Overview — {selectedMonth} {selectedYear}
                                </h6>
                                <Badge bg="light" text="dark" style={{ fontSize: '10px' }}>{filteredItems.length} items</Badge>
                            </Card.Header>
                            <Card.Body className="p-2 d-flex justify-content-center align-items-center">
                                {pieChartData.length === 0 ? (
                                    <div className="text-center text-muted py-4 w-100" style={{ fontSize: '12px' }}>No items found</div>
                                ) : (
                                    <div style={{ width: '100%', height: '350px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={pieChartData}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={120}
                                                    innerRadius={70}
                                                    label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : null}
                                                    labelLine={false}
                                                    paddingAngle={2}
                                                >
                                                    {pieChartData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <ReTooltip
                                                    contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #cbd5e1', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}
                                                    formatter={(value, name, props) => {
                                                        const entry = props.payload.payload;
                                                        if (entry.type === 'category') {
                                                            return [`${value} items in stock`, name];
                                                        }
                                                        const txn = itemTransactionStats[entry.id] || { issued: 0, returned: 0 };
                                                        return [`${value} ${entry.unit || ''} available | Issued: ${txn.issued}, Returned: ${txn.returned}`, name];
                                                    }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
 
                {/* Section 4: Compact Monthly Charts */}
                <Row className="g-3 mb-3">
                    <Col md={7}>
                        <Card className="border-0 shadow-sm" style={{ borderTop: '4px solid #0461FE', borderRadius: '8px' }}>
                            <Card.Header className="bg-transparent border-0 pt-3 px-3 pb-0">
                                <h6 className="fw-bold mb-0" style={{ fontSize: '13px', borderLeft: '3px solid #0461FE', paddingLeft: '8px' }}>Monthly Purchase vs Issue Qty</h6>
                            </Card.Header>
                            <Card.Body className="p-3">
                                <div style={{ width: '100%', height: '200px' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData} margin={{ left: -20, right: 10, top: 10, bottom: 5 }}>
                                            <XAxis dataKey="month" axisLine={false} tickLine={false} style={{ fontSize: '10px', fill: '#757575', fontWeight: 'bold' }} />
                                            <YAxis axisLine={false} tickLine={false} style={{ fontSize: '10px', fill: '#757575' }} />
                                            <ReTooltip contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #cbd5e1', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }} />
                                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                                            <Bar dataKey="purchased" name="Purchased" fill="#10b981" radius={[3, 3, 0, 0]} barSize={12} />
                                            <Bar dataKey="issued" name="Issued" fill="#0461FE" radius={[3, 3, 0, 0]} barSize={12} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={5}>
                        <Card className="border-0 shadow-sm" style={{ borderTop: '4px solid #f43f5e', borderRadius: '8px' }}>
                            <Card.Header className="bg-transparent border-0 pt-3 px-3 pb-0">
                                <h6 className="fw-bold mb-0" style={{ fontSize: '13px', borderLeft: '3px solid #f43f5e', paddingLeft: '8px' }}>Stock Movement Trend</h6>
                            </Card.Header>
                            <Card.Body className="p-3">
                                <div style={{ width: '100%', height: '200px' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData} margin={{ left: -20, right: 10, top: 10, bottom: 5 }}>
                                            <XAxis dataKey="month" axisLine={false} tickLine={false} style={{ fontSize: '10px', fill: '#757575', fontWeight: 'bold' }} />
                                            <YAxis axisLine={false} tickLine={false} style={{ fontSize: '10px', fill: '#757575' }} />
                                            <ReTooltip contentStyle={{ fontSize: '11px', borderRadius: '6px', border: '1px solid #cbd5e1', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }} />
                                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                                            <Line type="monotone" dataKey="purchased" name="Purchased" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                                            <Line type="monotone" dataKey="issued" name="Issued" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
 
            </Container>
 
            <style>{`
                .inv-dashboard-wrapper, .inv-dashboard-wrapper * {
                    font-family: 'Poppins', sans-serif !important;
                }
                .inv-dashboard-wrapper {
                    background: transparent !important;
                }
 
                .inv-header-bar {
                    background: #ffffff !important;
                    border: 1px solid var(--cardHeaderBg) !important;
                    border-left: 5px solid var(--primaryColor) !important;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
                    border-radius: 6px !important;
                }
 
                .inv-pulse-dot {
                    width: 8px; height: 8px; position: relative;
                }
                .inv-pulse-dot span {
                    width: 8px; height: 8px; background: var(--primaryColor);
                    border-radius: 50%; display: block;
                }
 
                .inv-refresh-btn {
                    background: var(--whiteColor); border: 1px solid var(--cardHeaderBg);
                    border-radius: 6px; color: var(--grayColor); transition: all 0.2s ease;
                }
                .inv-refresh-btn:hover {
                    background: #f8fafc; color: var(--primaryColor); border-color: var(--primaryColor); transform: rotate(45deg);
                }
 
                /* KPI Cards */
                .col-pbi { flex: 0 0 20%; max-width: 20%; }
                @media (max-width: 992px) { .col-pbi { flex: 0 0 50%; max-width: 50%; } }
                @media (max-width: 576px) { .col-pbi { flex: 0 0 100%; max-width: 100%; } }
 
                .inv-kpi-card {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    border: 1px solid rgba(226, 232, 240, 0.8) !important;
                    border-radius: 10px !important;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03) !important;
                }
                .inv-kpi-card:hover { transform: translateY(-4px); }
 
                .card-indigo { background: linear-gradient(135deg, rgba(99,102,241,0.04) 0%, rgba(99,102,241,0.1) 100%) !important; border-top: 4px solid #6366f1 !important; }
                .card-indigo:hover { box-shadow: 0 8px 16px rgba(99,102,241,0.12) !important; }
                .card-indigo .inv-kpi-icon { color: #6366f1 !important; background: rgba(99,102,241,0.15) !important; }
                .card-indigo .inv-kpi-value { color: #4f46e5 !important; }
                .card-indigo .inv-kpi-title { color: #6366f1 !important; }
 
                .card-amber { background: linear-gradient(135deg, rgba(255,159,67,0.04) 0%, rgba(255,159,67,0.1) 100%) !important; border-top: 4px solid #ff9f43 !important; }
                .card-amber:hover { box-shadow: 0 8px 16px rgba(255,159,67,0.12) !important; }
                .card-amber .inv-kpi-icon { color: #ff9f43 !important; background: rgba(255,159,67,0.15) !important; }
                .card-amber .inv-kpi-value { color: #d97706 !important; }
                .card-amber .inv-kpi-title { color: #d97706 !important; }
 
                .card-emerald { background: linear-gradient(135deg, rgba(16,185,129,0.04) 0%, rgba(16,185,129,0.1) 100%) !important; border-top: 4px solid #10b981 !important; }
                .card-emerald:hover { box-shadow: 0 8px 16px rgba(16,185,129,0.12) !important; }
                .card-emerald .inv-kpi-icon { color: #10b981 !important; background: rgba(16,185,129,0.15) !important; }
                .card-emerald .inv-kpi-value { color: #059669 !important; }
                .card-emerald .inv-kpi-title { color: #059669 !important; }
 
                .card-cobalt { background: linear-gradient(135deg, rgba(4,97,254,0.04) 0%, rgba(4,97,254,0.1) 100%) !important; border-top: 4px solid #0461FE !important; }
                .card-cobalt:hover { box-shadow: 0 8px 16px rgba(4,97,254,0.12) !important; }
                .card-cobalt .inv-kpi-icon { color: #0461FE !important; background: rgba(4,97,254,0.15) !important; }
                .card-cobalt .inv-kpi-value { color: #093375 !important; }
                .card-cobalt .inv-kpi-title { color: #0461FE !important; }
 
                .card-crimson { background: linear-gradient(135deg, rgba(244,63,94,0.04) 0%, rgba(244,63,94,0.1) 100%) !important; border-top: 4px solid #f43f5e !important; }
                .card-crimson:hover { box-shadow: 0 8px 16px rgba(244,63,94,0.12) !important; }
                .card-crimson .inv-kpi-icon { color: #f43f5e !important; background: rgba(244,63,94,0.15) !important; }
                .card-crimson .inv-kpi-value { color: #9f1239 !important; }
                .card-crimson .inv-kpi-title { color: #f43f5e !important; }
 
                .inv-kpi-icon {
                    font-size: 20px; margin-bottom: 6px;
                    width: 38px; height: 38px; display: inline-flex;
                    justify-content: center; align-items: center;
                    border-radius: 8px; margin-left: auto; margin-right: auto;
                    transition: all 0.3s ease;
                }
                .inv-kpi-card:hover .inv-kpi-icon { transform: scale(1.1) rotate(5deg); }
                .inv-kpi-value { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; }
                .inv-kpi-title { font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
 
                /* Filter Pills */
                .inv-filter-pill {
                    font-size: 10px; font-weight: 500; padding: 3px 10px;
                    border-radius: 4px; border: 1px solid #cbd5e1 !important;
                    background-color: #ffffff !important; color: #475569 !important;
                    transition: all 0.15s ease;
                }
                .inv-filter-pill.active, .inv-filter-pill:hover {
                    background: var(--primaryColor) !important; color: #ffffff !important;
                    border-color: var(--primaryColor) !important;
                }
 
                /* Category Cards */
                .inv-cat-card {
                    background: #ffffff !important;
                    border: 1px solid rgba(226,232,240,0.8) !important;
                    border-radius: 8px !important;
                    transition: all 0.25s ease !important;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.03) !important;
                }
                .inv-cat-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 6px 12px rgba(0,0,0,0.06) !important;
                }
                .inv-cat-card.active {
                    border-color: var(--primaryColor) !important;
                    box-shadow: 0 4px 12px rgba(4,97,254,0.1) !important;
                }
                .inv-cat-icon {
                    font-size: 18px; width: 32px; height: 32px;
                    display: inline-flex; justify-content: center; align-items: center;
                    border-radius: 6px; margin-bottom: 4px;
                    margin-left: auto; margin-right: auto;
                }
 
                /* Items Table */
                .inv-items-table {
                    font-size: 12px !important;
                }
                .inv-items-table thead th {
                    background: #f8fafc !important;
                    color: #475569 !important;
                    font-size: 10px !important;
                    font-weight: 700 !important;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    border-bottom: 2px solid #e2e8f0 !important;
                    padding: 8px 10px !important;
                    white-space: nowrap;
                }
                .inv-items-table tbody td {
                    font-size: 12px !important;
                    padding: 6px 10px !important;
                    vertical-align: middle !important;
                    border-bottom: 1px solid #f1f5f9 !important;
                }
                .inv-items-table tbody tr:hover {
                    background: rgba(4, 97, 254, 0.02) !important;
                }
                .inv-low-stock-row {
                    background: rgba(244, 63, 94, 0.04) !important;
                }
                .inv-low-stock-row:hover {
                    background: rgba(244, 63, 94, 0.08) !important;
                }
            `}</style>
        </div>
    );
};
 
export default InventoryDashboard;
 
 